/* Sale carousel — Swiper init, custom scrollbar, promo countdown, and card actions.
   Shuffle samples from the whole collection by fetching remaining paginate pages. */

const ROOT_SELECTOR = '[data-sale-carousel]'
const MAX_POOL_PAGES = 20
const SCROLLBAR_EVENTS = ['init', 'resize', 'setTranslate', 'transitionEnd', 'update', 'slideChange']
const DESKTOP_ROWS_MQ = '(min-width: 990px)'
const GRID_MODE_CLASS = 'orp-sale-carousel--grid-active'
const CTB_SLIDE_CLASS = 'orp-ctb__slide'
const MEASURING_CLASS = 'is-measuring-rows'

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

function pad2(value) {
  return String(value).padStart(2, '0')
}

function parseCountdownEnd(raw) {
  if (!raw) {
    const end = new Date()
    end.setHours(23, 59, 59, 999)
    return end
  }

  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const parsed = new Date(normalized)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function destroyCountdowns(root) {
  root.querySelectorAll('[data-sale-countdown]').forEach((el) => {
    if (el._saleCountdownTimer) {
      window.clearInterval(el._saleCountdownTimer)
      el._saleCountdownTimer = null
    }
  })
}

function initCountdowns(root) {
  destroyCountdowns(root)

  root.querySelectorAll('[data-sale-countdown]').forEach((el) => {
    const end = parseCountdownEnd(el.dataset.countdownEnd)
    const daysEl = el.querySelector('[data-countdown-days]')
    const hoursEl = el.querySelector('[data-countdown-hours]')
    const minutesEl = el.querySelector('[data-countdown-minutes]')
    const secondsEl = el.querySelector('[data-countdown-seconds]')
    if (!end || !daysEl || !hoursEl || !minutesEl || !secondsEl) return

    const setZeros = () => {
      daysEl.textContent = '00'
      hoursEl.textContent = '00'
      minutesEl.textContent = '00'
      secondsEl.textContent = '00'
    }

    const tick = () => {
      const diff = end.getTime() - Date.now()
      if (diff <= 0) {
        setZeros()
        window.clearInterval(el._saleCountdownTimer)
        el._saleCountdownTimer = null
        return
      }

      const totalSeconds = Math.floor(diff / 1000)
      const days = Math.floor(totalSeconds / 86400)
      const hours = Math.floor((totalSeconds % 86400) / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      daysEl.textContent = pad2(days)
      hoursEl.textContent = pad2(hours)
      minutesEl.textContent = pad2(minutes)
      secondsEl.textContent = pad2(seconds)
    }

    tick()
    el._saleCountdownTimer = window.setInterval(tick, 1000)
  })
}

function unbindScrollbar(root) {
  const swiper = root._saleSwiper
  const update = root._saleScrollbarUpdate
  if (!swiper || !update) return
  SCROLLBAR_EVENTS.forEach((eventName) => {
    try {
      swiper.off(eventName, update)
    } catch {
      /* ignore */
    }
  })
  root._saleScrollbarUpdate = null
}

function bindScrollbar(swiper, root) {
  const track = root.querySelector('[data-sale-scrollbar]')
  const drag = root.querySelector('[data-sale-scrollbar-drag]')
  if (!track || !drag) return

  unbindScrollbar(root)

  const update = () => {
    const view = swiper.width || 0
    const total = swiper.virtualSize || swiper.wrapperEl?.scrollWidth || 0
    if (!view || !total || total <= view + 1) {
      track.classList.add('is-locked')
      drag.style.width = '0'
      drag.style.transform = 'translate3d(0, 0, 0)'
      return
    }

    track.classList.remove('is-locked')
    const trackW = track.clientWidth
    const thumbW = Math.max((view / total) * trackW, 36)
    const maxX = Math.max(trackW - thumbW, 0)
    const progress = Math.min(1, Math.max(0, -swiper.translate / (total - view)))

    drag.style.width = `${thumbW}px`
    drag.style.transform = `translate3d(${progress * maxX}px, 0, 0)`
  }

  root._saleScrollbarUpdate = update
  SCROLLBAR_EVENTS.forEach((eventName) => swiper.on(eventName, update))
  update()
}

function getScrollState(swiper) {
  const view = swiper.width || 0
  const total = swiper.virtualSize || swiper.wrapperEl?.scrollWidth || 0
  const scrollable = view > 0 && total > view + 1

  if (!scrollable) {
    return { scrollable: false, atStart: true, atEnd: true }
  }

  const minT = swiper.minTranslate()
  const maxT = swiper.maxTranslate()
  const translate = swiper.translate

  return {
    scrollable: true,
    atStart: translate >= minT - 1,
    atEnd: translate <= maxT + 1,
  }
}

function unbindNavigation(root) {
  const swiper = root._saleSwiper
  const sync = root._saleNavSync
  if (swiper && sync) {
    SCROLLBAR_EVENTS.forEach((eventName) => {
      try {
        swiper.off(eventName, sync)
      } catch {
        /* ignore */
      }
    })
  }

  const prevEl = root._saleNavPrev
  const nextEl = root._saleNavNext
  const onPrev = root._saleNavOnPrev
  const onNext = root._saleNavOnNext
  if (prevEl && onPrev) prevEl.removeEventListener('click', onPrev)
  if (nextEl && onNext) nextEl.removeEventListener('click', onNext)
  root._saleNavPrev = null
  root._saleNavNext = null
  root._saleNavOnPrev = null
  root._saleNavOnNext = null
  root._saleNavSync = null
}

function bindNavigation(swiper, root) {
  const prevEl = root.querySelector('[data-sale-prev]')
  const nextEl = root.querySelector('[data-sale-next]')
  if (!prevEl || !nextEl) return

  unbindNavigation(root)

  const sync = () => {
    const { scrollable, atStart, atEnd } = getScrollState(swiper)

    prevEl.disabled = !scrollable || atStart
    nextEl.disabled = !scrollable || atEnd
    prevEl.classList.toggle('is-at-edge', !scrollable || atStart)
    nextEl.classList.toggle('is-at-edge', !scrollable || atEnd)
    prevEl.classList.remove('swiper-button-disabled', 'swiper-button-lock')
    nextEl.classList.remove('swiper-button-disabled', 'swiper-button-lock')
    prevEl.setAttribute('aria-disabled', !scrollable || atStart ? 'true' : 'false')
    nextEl.setAttribute('aria-disabled', !scrollable || atEnd ? 'true' : 'false')
    root.querySelector('.orp-sale-carousel__nav')?.classList.toggle('is-not-scrollable', !scrollable)
  }

  const onPrev = (event) => {
    event.preventDefault()
    if (prevEl.disabled) return
    swiper.slidePrev()
  }
  const onNext = (event) => {
    event.preventDefault()
    if (nextEl.disabled) return
    swiper.slideNext()
  }

  prevEl.addEventListener('click', onPrev)
  nextEl.addEventListener('click', onNext)
  root._saleNavPrev = prevEl
  root._saleNavNext = nextEl
  root._saleNavOnPrev = onPrev
  root._saleNavOnNext = onNext
  root._saleNavSync = sync

  SCROLLBAR_EVENTS.forEach((eventName) => swiper.on(eventName, sync))
  sync()
}

function destroySwiper(root) {
  unbindScrollbar(root)
  unbindNavigation(root)

  const resizeSync = root._saleGridResizeSync
  const swiper = root._saleSwiper
  if (swiper && resizeSync) {
    try {
      swiper.off('resize', resizeSync)
    } catch {
      /* ignore */
    }
  }
  root._saleGridResizeSync = null

  const instance = root._saleSwiper
  if (!instance) return
  try {
    instance.destroy(true, true)
  } catch {
    /* ignore */
  }
  root._saleSwiper = null
}

function getCarouselRows(root) {
  return parseInt(root.dataset.carouselRows, 10) || 1
}

function useSwiperGrid(root) {
  return getCarouselRows(root) > 1 && window.matchMedia(DESKTOP_ROWS_MQ).matches
}

function getTargetCardWidth(root) {
  const isDesktop = window.matchMedia(DESKTOP_ROWS_MQ).matches
  const dataKey = isDesktop ? 'cardWidth' : 'cardWidthMobile'
  const fromData = parseInt(root.dataset[dataKey], 10)
  if (fromData > 0) return fromData

  const fromCss = parseFloat(getComputedStyle(root).getPropertyValue('--orp-ctb-slide-width'))
  return fromCss > 0 ? fromCss : 240
}

function getGap(root) {
  return parseInt(root.dataset.gap, 10) || 16
}

function getGridSlidesPerView(swiperEl, root) {
  const gap = getGap(root)
  const width = swiperEl.clientWidth
  const cardWidth = getTargetCardWidth(root)

  if (!width || !cardWidth) return 2

  return Math.max(1, Math.floor((width + gap) / (cardWidth + gap)))
}

/* Swiper Grid sizes slides with `height: calc((100% - gap) / rows)`, a percentage of the
   container. Without an explicit container height that resolves to auto and the
   column-wrap flex track never wraps, so every card lands in one column. */
function syncGridHeight(swiperEl, root) {
  if (!useSwiperGrid(root)) {
    swiperEl.style.height = ''
    return
  }

  const gap = getGap(root)
  const rows = getCarouselRows(root)
  const columns = getGridSlidesPerView(swiperEl, root)
  const slideWidth = (swiperEl.clientWidth - gap * (columns - 1)) / columns
  const slides = Array.from(swiperEl.querySelectorAll('.swiper-slide'))
  if (!slides.length || slideWidth <= 0) return

  const inlineWidths = slides.map((slide) => slide.style.width)
  swiperEl.classList.add(MEASURING_CLASS)
  slides.forEach((slide) => {
    slide.style.width = `${slideWidth}px`
  })

  let rowHeight = 0
  slides.forEach((slide) => {
    rowHeight = Math.max(rowHeight, slide.getBoundingClientRect().height)
  })

  slides.forEach((slide, index) => {
    slide.style.width = inlineWidths[index]
  })
  swiperEl.classList.remove(MEASURING_CLASS)

  if (!rowHeight) return

  swiperEl.style.height = `${Math.ceil(rowHeight) * rows + gap * (rows - 1)}px`
}

function buildSwiperConfig(swiperEl, root, calm, autoplayOn) {
  const shared = {
    spaceBetween: getGap(root),
    speed: calm ? 1 : 400,
    grabCursor: true,
    watchOverflow: false,
    touchMoveStopPropagation: true,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    mousewheel: {
      forceToAxis: true,
      releaseOnEdges: true,
    },
    autoplay: autoplayOn
      ? {
        delay: parseInt(root.dataset.autoplayDelay, 10) || 5000,
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
      }
      : false,
  }

  if (useSwiperGrid(root)) {
    return {
      ...shared,
      slidesPerView: getGridSlidesPerView(swiperEl, root),
      grid: {
        rows: getCarouselRows(root),
        fill: 'column',
      },
      centerInsufficientSlides: false,
    }
  }

  return {
    ...shared,
    slidesPerView: 'auto',
    centerInsufficientSlides: true,
  }
}

function bindGridResizeSync(swiper, swiperEl, root) {
  const resizeSync = () => {
    if (!useSwiperGrid(root)) return

    const next = getGridSlidesPerView(swiperEl, root)
    if (swiper.params.slidesPerView !== next) {
      swiper.params.slidesPerView = next
    }

    syncGridHeight(swiperEl, root)

    try {
      swiper.update()
      root._saleScrollbarUpdate?.()
      root._saleNavSync?.()
    } catch {
      /* ignore */
    }
  }

  root._saleGridResizeSync = resizeSync
  swiper.on('resize', resizeSync)
}

function syncGridMode(root) {
  const gridOn = useSwiperGrid(root)
  root.classList.toggle(GRID_MODE_CLASS, gridOn)

  root.querySelectorAll('.swiper-slide').forEach((slide) => {
    slide.classList.toggle(CTB_SLIDE_CLASS, !gridOn)
  })
}

function ensureLayoutListener(root) {
  if (root._saleRowLayoutBound) return
  root._saleRowLayoutBound = true

  const mq = window.matchMedia(DESKTOP_ROWS_MQ)
  const onChange = () => {
    window.clearTimeout(root._saleRowLayoutTimer)
    root._saleRowLayoutTimer = window.setTimeout(() => {
      destroySwiper(root)
      syncGridMode(root)
      initSwiper(root)
    }, 150)
  }

  mq.addEventListener('change', onChange)
  root._saleRowLayoutMq = mq
  root._saleRowLayoutOnChange = onChange
}

function initSwiper(root) {
  const swiperEl = root.querySelector('[data-sale-swiper]')
  if (!swiperEl) return

  root._saleSwiperInitToken = (root._saleSwiperInitToken || 0) + 1
  const initToken = root._saleSwiperInitToken

  let attempts = 0
  const start = () => {
    if (initToken !== root._saleSwiperInitToken) return
    if (++attempts > 60) return
    const Swiper = window.Swiper
    if (!Swiper) {
      window.setTimeout(start, 50)
      return
    }

    if (useSwiperGrid(root) && !swiperEl.clientWidth) {
      window.setTimeout(start, 50)
      return
    }

    destroySwiper(root)
    syncGridMode(root)
    syncGridHeight(swiperEl, root)

    const calm = reducedMotion()
    const autoplayOn = root.dataset.autoplay === 'true' && !calm

    root._saleSwiper = new Swiper(swiperEl, buildSwiperConfig(swiperEl, root, calm, autoplayOn))

    if (useSwiperGrid(root)) {
      bindGridResizeSync(root._saleSwiper, swiperEl, root)
    }

    bindNavigation(root._saleSwiper, root)
    bindScrollbar(root._saleSwiper, root)

    const refresh = () => {
      try {
        syncGridHeight(swiperEl, root)
        root._saleSwiper.update()
        root._saleScrollbarUpdate?.()
        root._saleNavSync?.()
      } catch {
        /* ignore */
      }
    }

    window.requestAnimationFrame(refresh)
    window.setTimeout(refresh, 120)
    window.setTimeout(refresh, 400)

    swiperEl.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', refresh, { once: true })
      }
    })
  }

  start()
}

async function addToCart(variantId) {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: String(variantId), quantity: 1 }] }),
  })
  if (!response.ok) throw new Error('Could not add this product to cart.')
  document.dispatchEvent(new CustomEvent('nova:cart:refresh', { bubbles: true, composed: true }))
  document.dispatchEvent(new CustomEvent('nova:cart:open', { bubbles: true }))
}

function flashLabel(button, text) {
  const idle = button.dataset.saleIdleLabel || button.textContent
  button.dataset.saleIdleLabel = idle
  button.textContent = text
  window.setTimeout(() => {
    button.textContent = button.dataset.saleIdleLabel
  }, 1400)
}

function bindCardActions(root) {
  if (root._saleActionsBound) return
  root._saleActionsBound = true

  root.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-sale-card-action]')
    if (!button || button.disabled) return
    const card = button.closest('[data-sale-card]')
    if (!card) return

    if (card.dataset.hasVariants === 'true') {
      window.location.href = card.dataset.productUrl
      return
    }

    button.disabled = true
    try {
      await addToCart(card.dataset.variantId)
      flashLabel(button, 'Added!')
    } catch {
      flashLabel(button, 'Try again')
    } finally {
      button.disabled = false
    }
  })
}

function shuffleInPlace(items) {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const swap = items[i]
    items[i] = items[j]
    items[j] = swap
  }
  return items
}

function slideProductUrl(slide) {
  return slide.querySelector('[data-sale-card]')?.dataset.productUrl || ''
}

function poolPageUrl(sectionId, page) {
  const url = new URL(window.location.href)
  url.searchParams.set('section_id', sectionId)
  url.searchParams.set('page', String(page))
  return url.toString()
}

function parseSlidesFromSectionHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(doc.querySelectorAll('[data-sale-slide]'))
}

async function fetchExtraPoolPages(sectionId, pages) {
  const last = Math.min(pages, MAX_POOL_PAGES)
  const currentPage = parseInt(new URL(window.location.href).searchParams.get('page'), 10) || 1
  const requests = []

  for (let page = 1; page <= last; page++) {
    if (page === currentPage) continue
    requests.push(
      fetch(poolPageUrl(sectionId, page), {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      })
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => ''),
    )
  }

  const htmlPages = await Promise.all(requests)
  const slides = []

  htmlPages.forEach((html) => {
    if (!html) return
    parseSlidesFromSectionHtml(html).forEach((node) => {
      slides.push(document.importNode(node, true))
    })
  })

  return slides
}

function finishPoolTrack(track, slides, promo, promoLast) {
  const keep = document.createDocumentFragment()
  if (promo && !promoLast) keep.appendChild(promo)
  slides.forEach((slide) => keep.appendChild(slide))
  if (promo && promoLast) keep.appendChild(promo)
  track.appendChild(keep)
}

async function preparePoolNow(root) {
  const track = root.querySelector('[data-sale-track]')
  if (!track) {
    root.classList.remove('is-loading-pool')
    return
  }

  const pages = parseInt(root.dataset.poolPages, 10) || 1
  const shuffleOn = root.dataset.shuffle === 'true'
  const showCount = parseInt(root.dataset.showCount, 10) || 8
  const poolSize = parseInt(root.dataset.poolSize, 10) || 24
  const promoLast = root.dataset.promoPosition === 'last'
  const sectionId = root.dataset.sectionId
  const needsFullCollection = shuffleOn && pages > 1 && Boolean(sectionId)

  if (!needsFullCollection) {
    root.classList.remove('is-loading-pool')
    return
  }

  const promo = track.querySelector('[data-sale-promo]')
  let slides = Array.from(track.querySelectorAll('[data-sale-slide]'))

  try {
    const extra = await fetchExtraPoolPages(sectionId, pages)
    const seen = new Set(slides.map(slideProductUrl).filter(Boolean))
    extra.forEach((slide) => {
      const key = slideProductUrl(slide)
      if (key && seen.has(key)) return
      if (key) seen.add(key)
      slides.push(slide)
    })
  } catch {
    /* Keep the first page of slides when extra pages fail. */
  }

  shuffleInPlace(slides)
  slides = slides.slice(0, Math.min(poolSize, slides.length))
  slides = slides.slice(0, Math.min(showCount, slides.length))

  track.querySelectorAll('[data-sale-slide]').forEach((slide) => slide.remove())
  promo?.remove()
  finishPoolTrack(track, slides, promo, promoLast)
  root.classList.remove('is-loading-pool')
}

function preparePool(root) {
  if (!root._salePoolPromise) {
    root._salePoolPromise = preparePoolNow(root)
  }
  return root._salePoolPromise
}

function initRoot(root) {
  root._saleInitToken = (root._saleInitToken || 0) + 1
  const token = root._saleInitToken

  preparePool(root).then(() => {
    if (token !== root._saleInitToken) return
    initCountdowns(root)
    bindCardActions(root)
    destroySwiper(root)
    syncGridMode(root)
    initSwiper(root)
    ensureLayoutListener(root)
  })
}

function destroyRoot(root) {
  root._saleInitToken = (root._saleInitToken || 0) + 1
  root._salePoolPromise = null
  destroyCountdowns(root)
  destroySwiper(root)

  if (root._saleRowLayoutMq && root._saleRowLayoutOnChange) {
    root._saleRowLayoutMq.removeEventListener('change', root._saleRowLayoutOnChange)
  }
  window.clearTimeout(root._saleRowLayoutTimer)
  root._saleRowLayoutBound = false
  root._saleRowLayoutMq = null
  root._saleRowLayoutOnChange = null
}

function boot() {
  document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot)
} else {
  boot()
}

document.addEventListener('sale-carousel:ready', (event) => {
  const root = event.target.closest?.(ROOT_SELECTOR) || event.target.querySelector?.(ROOT_SELECTOR)
  if (root) initRoot(root)
})

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot)
})

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(destroyRoot)
})

document.addEventListener('shopify:section:select', (event) => {
  event.target.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
    try {
      root._saleSwiper?.update()
      root._saleScrollbarUpdate?.()
      root._saleNavSync?.()
    } catch {
      /* ignore */
    }
  })
})
